import { useId, useState, type ReactNode } from 'react';
import {
  Building2Icon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  EyeIcon,
  FolderPlusIcon,
  HandIcon,
  LockIcon,
  LogOutIcon,
  PencilIcon,
  ShieldIcon,
  ToggleRightIcon,
  Trash2Icon,
  UserCogIcon,
  UserMinusIcon,
  UserPlusIcon,
  UserXIcon,
  UsersRoundIcon,
  type LucideIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
// The module, not the barrel: a leaf with no imports of its own, and the same day
// headings the notifications page writes (spec 147 FR-003).
import { groupByDay } from '@/features/notifications/dayGroups';
import type { AccessLogEntry } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_SECONDARY,
  LIST_ROWS,
} from '@/shared/components/listRow';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { formatExactTime } from '@/shared/utils/time';

import {
  ORG_ADMIN_CHIP,
  foldAccessLog,
  type AccessLogItem,
  type AccessLogRollout,
  type AccessLogRow,
} from '../accessLog';

/**
 * Access-log entries as a timeline (spec 191, spec 194 US4): grouped by day, each
 * entry one line — who, what kind of change, the sentence, the time. Settings shows
 * one workspace's; the admin console shows every workspace's and names each
 * entry's (`workspaceOf`).
 *
 * The rollout's entries are folded (spec 193 FR-010): a workspace's two read as one
 * row, and — where the list is many workspaces', which `workspaceOf` says — one
 * rollout across them is one row that expands to its workspaces.
 */
export function AccessLogList({
  entries,
  label,
  workspaceOf,
  hasMore,
  loadingMore,
  onMore,
}: {
  entries: AccessLogEntry[];
  label: string;
  workspaceOf?: (entry: AccessLogEntry) => ReactNode;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}) {
  const items = foldAccessLog(entries, {
    acrossWorkspaces: workspaceOf !== undefined,
  });
  const days = groupByDay(items, itemAt);
  const headingId = useId();

  return (
    <div className="flex flex-col gap-2">
      <ol aria-label={label} className="flex flex-col gap-3">
        {days.map((day, index) => (
          <li key={day.key} className="flex flex-col gap-1">
            <h3
              id={`${headingId}-${String(index)}`}
              className="text-muted-foreground text-xs font-medium"
            >
              {day.label}
            </h3>
            <ol
              aria-labelledby={`${headingId}-${String(index)}`}
              className={LIST_ROWS}
            >
              {day.rows.map((item) =>
                item.type === 'rollout' ? (
                  <RolloutItem
                    key={item.id}
                    rollout={item}
                    workspaceOf={workspaceOf}
                  />
                ) : (
                  <li key={item.entry.id}>
                    <TimelineRow row={item} workspaceOf={workspaceOf} />
                  </li>
                ),
              )}
            </ol>
          </li>
        ))}
      </ol>
      {hasMore ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={loadingMore}
          onClick={onMore}
        >
          {loadingMore ? 'Loading…' : 'Show older'}
        </Button>
      ) : null}
    </div>
  );
}

function itemAt(item: AccessLogItem): string {
  return item.type === 'rollout' ? item.at : item.entry.at;
}

/**
 * The time of day: the day is the heading above, so "3 days ago" would say it
 * twice. Hoisted, as the shared formatters are: built once, not per row.
 */
const TIME_OF_DAY = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function When({ at }: { at: string }) {
  const moment = new Date(at);
  return (
    <time
      dateTime={at}
      title={formatExactTime(at)}
      className="text-muted-foreground shrink-0 text-xs tabular-nums"
    >
      {Number.isNaN(moment.getTime()) ? '' : TIME_OF_DAY.format(moment)}
    </time>
  );
}

/**
 * The kind of change, as a line icon (brand FD-15a: stroke 1.5). Beside the
 * sentence, not instead of it, so it is decorative; `data-kind` names it for tests.
 */
const KIND_ICON: Record<AccessLogEntry['kind'], LucideIcon> = {
  workspace_created: FolderPlusIcon,
  workspace_deleted: Trash2Icon,
  members_backfilled: UsersRoundIcon,
  members_review_requested: ClipboardCheckIcon,
  member_added: UserPlusIcon,
  member_removed: UserMinusIcon,
  member_left: LogOutIcon,
  role_changed: UserCogIcon,
  visibility_changed: Building2Icon,
  renamed: PencilIcon,
  access_requested: HandIcon,
  access_request_dismissed: UserXIcon,
  admin_opened: EyeIcon,
  admin_acted: ShieldIcon,
};

function iconOf(row: AccessLogRow): { Icon: LucideIcon; kind: string } {
  // A workspace's two rollout entries are one event: access turned on.
  if (row.entries.length > 1) return { Icon: ToggleRightIcon, kind: 'rollout' };
  const { entry } = row;
  if (entry.kind === 'visibility_changed' && entry.detail?.to === 'private') {
    return { Icon: LockIcon, kind: entry.kind };
  }
  return { Icon: KIND_ICON[entry.kind] ?? ToggleRightIcon, kind: entry.kind };
}

function KindIcon({ Icon, kind }: { Icon: LucideIcon; kind: string }) {
  return (
    <Icon
      aria-hidden="true"
      data-kind={kind}
      strokeWidth={1.5}
      className="text-muted-foreground size-3.5 shrink-0"
    />
  );
}

/**
 * Who did it. The rollout's entries are by no one, so the disc is left empty
 * rather than given a name the sentence does not; the column still lines up.
 */
function Actor({ actor }: { actor: AccessLogEntry['actor'] }) {
  return actor ? (
    <UserAvatar
      id={actor.id}
      displayName={actor.display_name}
      email={actor.email}
    />
  ) : (
    <span aria-hidden className="bg-muted size-5 shrink-0 rounded-full" />
  );
}

/** One line: who, the kind, the sentence (with its workspace and mark), when. */
function TimelineRow({
  row,
  workspaceOf,
}: {
  row: AccessLogRow;
  workspaceOf?: (entry: AccessLogEntry) => ReactNode;
}) {
  return (
    <div className={LIST_ROW}>
      <Actor actor={row.entry.actor} />
      <KindIcon {...iconOf(row)} />
      <Sentence
        sentence={row.sentence}
        workspace={workspaceOf?.(row.entry)}
        asOrgAdmin={row.entry.as_org_admin === true}
      />
      <When at={row.entry.at} />
    </div>
  );
}

function Sentence({
  sentence,
  workspace,
  asOrgAdmin,
  children,
}: {
  sentence: string;
  workspace?: ReactNode;
  asOrgAdmin?: boolean;
  children?: ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className="text-foreground text-sm">{sentence}</span>
      {asOrgAdmin ? (
        <Badge variant="outline" title={ORG_ADMIN_CHIP.title}>
          {ORG_ADMIN_CHIP.text}
        </Badge>
      ) : null}
      {workspace ? (
        <span className={LIST_ROW_SECONDARY}>{workspace}</span>
      ) : null}
      {children}
    </span>
  );
}

/**
 * One rollout across workspaces: a sentence with its count, and the workspaces
 * behind a disclosure, so the day the switch went on fits on one screen (spec 193
 * SC-003). Its workspaces' entries are listed only inside it, never again below.
 */
function RolloutItem({
  rollout,
  workspaceOf,
}: {
  rollout: AccessLogRollout;
  workspaceOf?: (entry: AccessLogEntry) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const listId = `${rollout.id}-workspaces`;
  // Workspaces, not rows: a workspace whose two entries fell on different pages is
  // two rows here, and the sentence above counts it once.
  const count = new Set(rollout.workspaces.map((row) => row.entry.workspace.id))
    .size;

  return (
    <li className="flex flex-col">
      <div className={LIST_ROW}>
        <Actor actor={null} />
        <KindIcon Icon={ToggleRightIcon} kind="rollout" />
        <Sentence sentence={rollout.sentence}>
          <Button
            variant="link"
            size="sm"
            className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-xs"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            onClick={() => setOpen((now) => !now)}
          >
            <ChevronRightIcon
              aria-hidden="true"
              strokeWidth={1.5}
              className={cn(
                'size-3.5 transition-transform duration-[var(--motion-duration-base)]',
                open && 'rotate-90',
              )}
            />
            {open
              ? 'Hide the workspaces'
              : `Show the ${String(count)} workspaces`}
          </Button>
        </Sentence>
        <When at={rollout.at} />
      </div>
      {open ? (
        <ol
          id={listId}
          aria-label="Workspaces set up"
          className="border-border mb-2 ml-2 flex flex-col border-l pl-5"
        >
          {rollout.workspaces.map((row) => (
            <li key={row.entry.id}>
              <TimelineRow row={row} workspaceOf={workspaceOf} />
            </li>
          ))}
        </ol>
      ) : null}
    </li>
  );
}
